import { useCallback, useRef, useState, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

import {
    clampBreakpointValue,
    type KpiDataRange,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { getValueFromPercent } from 'components/molecules/PackArchitecture/kpiRangeSlider.utils';

type UseKpiBreakpointDragParams = {
    trackRef: RefObject<HTMLDivElement>;
    dataRange: KpiDataRange;
    min: number;
    max: number;
    breakpoints: number[];
    onBreakpointsChange: (breakpoints: number[]) => void;
};

export const useKpiBreakpointDrag = ({
    trackRef,
    dataRange,
    min,
    max,
    breakpoints,
    onBreakpointsChange,
}: UseKpiBreakpointDragParams) => {
    const [activeBreakpointIndex, setActiveBreakpointIndex] = useState<number | null>(null);
    const breakpointsRef = useRef(breakpoints);
    breakpointsRef.current = breakpoints;

    const resolveValueFromPointer = useCallback(
        (clientX: number) => {
            const track = trackRef.current;
            if (!track) return dataRange.defaultMin;
            const { left, width } = track.getBoundingClientRect();
            const ratio = Math.max(0, Math.min(1, (clientX - left) / width));
            return getValueFromPercent(ratio * 100, dataRange);
        },
        [dataRange, trackRef],
    );

    const startBreakpointDrag = (index: number) => (event: ReactPointerEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setActiveBreakpointIndex(index);
        event.currentTarget.setPointerCapture(event.pointerId);

        const sortedBreakpoints = [...breakpointsRef.current].sort((left, right) => left - right);

        const onPointerMove = (moveEvent: globalThis.PointerEvent) => {
            const nextValue = resolveValueFromPointer(moveEvent.clientX);
            const clamped = clampBreakpointValue(nextValue, index, min, max, sortedBreakpoints);
            const updated = sortedBreakpoints.map((breakpoint, currentIndex) =>
                currentIndex === index ? clamped : breakpoint,
            );
            sortedBreakpoints[index] = clamped;
            onBreakpointsChange(updated);
        };

        const onPointerUp = () => {
            setActiveBreakpointIndex(null);
            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
        };

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
    };

    return { activeBreakpointIndex, startBreakpointDrag };
};
